import { CareTeamProviderType } from '../constants/patients.constants';
import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useGetAllProvidersV2Query } from '~/store/services/providers';
import { useGetProviderAssignmentRecommendationsQuery } from '~/store/services/providerAssignments';
import { buildProviderMapFromGetProviderByIdResponse } from '~/components/maud/providerAssignment/utils';
import {
  extractIqApiErrorMessage,
  parseMissingFieldErrors,
  MissingFieldError,
} from '../util/api-error.util';
import { getTimezoneAbbreviation } from '~/lib/util/timezone.util';

export type ProviderRecommendationsProps = {
  patientId: string;
  providerTypes: CareTeamProviderType[];
  skip: boolean;
};

export type ProviderRecommendations = {
  recommendationsData: GetProviderAssignmentRecommendationsResponse;
  isError: boolean;
  isFetching: boolean;
  iqErrorMessage?: string;
  missingFields?: MissingFieldError[];
  providerMap: Record<string, GetProviderByIdResponse>;
};

const useProviderRecommendations = ({
  patientId,
  providerTypes,
  skip,
}: ProviderRecommendationsProps): ProviderRecommendations => {
  // Serialize provider types to a stable string for consistent query caching
  const providerTypesKey = useMemo(
    () =>
      providerTypes && providerTypes.length > 0
        ? [...providerTypes].sort((a, b) => a.localeCompare(b)).join(',')
        : '',
    [providerTypes],
  );

  const normalizedProviderTypes = useMemo(
    () =>
      providerTypesKey
        ? (providerTypesKey.split(',') as CareTeamProviderType[])
        : [],
    [providerTypesKey],
  );

  // Skip query if conditions not met
  const shouldSkip = skip || !patientId || normalizedProviderTypes.length === 0;

  // Fetch provider recommendations using RTK Query's built-in caching
  const {
    data: recommendationsData,
    error: recommendationsError,
    isFetching: isRecommendationsFetching,
  } = useGetProviderAssignmentRecommendationsQuery(
    shouldSkip
      ? skipToken
      : {
          patientId,
          providerTypes: normalizedProviderTypes,
        },
  );

  // Extract IQ API error message from recommendations query
  // Clear error message during fetching to prevent stale errors
  const iqErrorMessage = useMemo(() => {
    if (isRecommendationsFetching) return undefined;
    return recommendationsError
      ? extractIqApiErrorMessage(recommendationsError)
      : undefined;
  }, [isRecommendationsFetching, recommendationsError]);

  // Extract missing fields from recommendations error
  const missingFields = useMemo(() => {
    if (isRecommendationsFetching) return undefined;
    return recommendationsError
      ? parseMissingFieldErrors(recommendationsError)
      : undefined;
  }, [isRecommendationsFetching, recommendationsError]);

  // Extract provider IDs from recommendations
  const allProviderIds = useMemo(() => {
    if (!recommendationsData?.assignmentOptions) {
      return [];
    }
    return Array.from(
      new Set(
        recommendationsData.assignmentOptions.flatMap((o: any) =>
          o.tiers.flatMap((t: any) =>
            t.providers.map((p: any) => p.userId as string),
          ),
        ),
      ),
    );
  }, [recommendationsData]);

  // Fetch provider details only when we have IDs
  const {
    data: providersData,
    error: providersError,
    isFetching: isProvidersFetching,
  } = useGetAllProvidersV2Query(
    allProviderIds.length === 0
      ? skipToken
      : {
          id: allProviderIds,
          'page-number': 1,
          'page-size': allProviderIds.length,
        },
  );

  // Extract timezone data from IQ API recommendations (IANA format -> abbreviation)
  const iqTimezoneMap = useMemo(() => {
    if (!recommendationsData?.assignmentOptions) {
      return {};
    }
    const timezoneMap: Record<string, string> = {};
    recommendationsData.assignmentOptions.forEach((option: any) => {
      option.tiers.forEach((tier: any) => {
        tier.providers.forEach((provider: any) => {
          if (provider.userId && provider.matchFactors?.timeZoneOfPractice) {
            // Convert IANA timezone to abbreviation (e.g., "America/Los_Angeles" => "PST")
            timezoneMap[provider.userId] = getTimezoneAbbreviation(
              provider.matchFactors.timeZoneOfPractice,
            );
          }
        });
      });
    });
    return timezoneMap;
  }, [recommendationsData]);

  const iqPrimaryTagMap = useMemo(() => {
    if (!recommendationsData?.assignmentOptions) {
      return {};
    }
    const primaryTagMap: Record<string, string> = {};
    recommendationsData.assignmentOptions.forEach((option: any) => {
      option.tiers.forEach((tier: any) => {
        tier.providers.forEach((provider: any) => {
          if (provider.userId && provider.matchFactors?.primaryTag) {
            primaryTagMap[provider.userId] = provider.matchFactors.primaryTag;
          }
        });
      });
    });
    return primaryTagMap;
  }, [recommendationsData]);

  // Build provider map from fetched data and override with IQ API timezone
  const providerMap = useMemo(() => {
    if (!providersData?.data) {
      return null;
    }
    const baseMap = buildProviderMapFromGetProviderByIdResponse(
      providersData.data,
    );

    // Override timezone with IQ API data (converted to abbreviation)
    Object.keys(iqTimezoneMap).forEach((userId) => {
      if (baseMap[userId]) {
        baseMap[userId] = {
          ...baseMap[userId],
          timeZoneDetails: {
            ...baseMap[userId].timeZoneDetails,
            code: iqTimezoneMap[userId],
          },
        };
      }
    });

    // Merge primaryTag from IQ API data
    Object.keys(iqPrimaryTagMap).forEach((userId) => {
      if (baseMap[userId]) {
        baseMap[userId] = {
          ...baseMap[userId],
          primaryTag: iqPrimaryTagMap[userId],
        };
      }
    });

    return baseMap;
  }, [providersData, iqTimezoneMap, iqPrimaryTagMap]);

  return {
    recommendationsData: recommendationsData ?? null,
    isError: !!recommendationsError || !!providersError,
    isFetching: isRecommendationsFetching || isProvidersFetching,
    iqErrorMessage,
    missingFields,
    providerMap,
  };
};

export default useProviderRecommendations;
